import { useState, useEffect } from 'react';
import { getProducts, getPriceHistory } from '../services/api';
import { PriceHistoryEntry } from '../types';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend
} from 'recharts';

export function HistoryModal() {
  const [products, setProducts] = useState<Array<{id: number; name: string}>>([]);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [priceHistory, setPriceHistory] = useState<PriceHistoryEntry[]>([]);
  const [days, setDays] = useState(90);
  const [loading, setLoading] = useState(false);

  // Load products for selector
  useEffect(() => {
    const loadProducts = async () => {
      try {
        const data = await getProducts();
        setProducts(data.map((p) => ({ id: p.id, name: p.custom_name })));
        if (data.length > 0 && !selectedProductId) {
          setSelectedProductId(data[0].id);
        }
      } catch (error) {
        console.error('Failed to load products:', error);
      }
    };
    loadProducts();
  }, [selectedProductId]);

  // Load price history when product or days change
  useEffect(() => {
    if (!selectedProductId) return;

    const loadHistory = async () => {
      setLoading(true);
      try {
        const data = await getPriceHistory(selectedProductId, days);
        setPriceHistory(data);
      } catch (error) {
        console.error('Failed to load price history:', error);
        setPriceHistory([]);
      } finally {
        setLoading(false);
      }
    };

    loadHistory();
  }, [selectedProductId, days]);

  if (products.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400">Aucun produit disponible pour afficher l'historique</p>
      </div>
    );
  }

  // Format data for chart
  const chartData = priceHistory
    .slice()
    .sort((a, b) => new Date(a.checked_at).getTime() - new Date(b.checked_at).getTime())
    .map((entry) => ({
      date: new Date(entry.checked_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }),
      price: entry.price,
    }));

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-4">
        <select
          value={selectedProductId || ''}
          onChange={(e) => setSelectedProductId(e.target.value ? parseInt(e.target.value) : null)}
          className="input flex-1"
        >
          <option value="">Sélectionner un produit</option>
          {products.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        <select
          value={days}
          onChange={(e) => setDays(parseInt(e.target.value))}
          className="input w-auto"
        >
          <option value={7}>7 jours</option>
          <option value={30}>30 jours</option>
          <option value={90}>90 jours</option>
          <option value={365}>1 an</option>
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      ) : chartData.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p>Pas de données d'historique pour cette période</p>
          <p className="text-sm mt-2">Les prix s'afficheront après les prochaines vérifications</p>
        </div>
      ) : (
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis
                dataKey="date"
                tick={{ fill: '#9ca3af', fontSize: 11 }}
                axisLine={{ stroke: '#374151' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: '#9ca3af', fontSize: 11 }}
                axisLine={{ stroke: '#374151' }}
                tickLine={false}
                tickFormatter={(value) => `${value.toFixed(2)}€`}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1f2937',
                  border: '1px solid #374151',
                  borderRadius: '8px',
                  color: '#fff',
                }}
                formatter={(value: number) => [`${value.toFixed(2)} €`, 'Prix']}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="price"
                stroke="#22c55e"
                strokeWidth={2}
                dot={{ fill: '#22c55e', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, fill: '#22c55e' }}
                name="Prix"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}